import React, { useContext, useEffect, useState } from "react";
import Annotation from "@canvas-panel/core/lib/components/Annotation/Annotation";
import EditableAnnotation from "@canvas-panel/core/lib/components/EditableAnnotation/EditableAnnotation";
import IIIFCanvas from "../../canvas/components/IIIFCanvas";
import CanvasNavigation from "../../canvas/components/CanvasNavigation";
import { AnnotationContext } from "../../annotation/components/Annotation/AnnotationServiceProvider";
import { useAnnotationsToggle } from "../../annotation/hooks";
import { createAnnotation, getBoxStyles } from "../../annotation/helpers";
import AnnotationDetailPopup from "../../annotation/components/Annotation/AnnotationDetailPopup";
import { useSearcher } from "../../search/components/SearcherContext";
import { SEARCH } from "../../config";

const osdOptions = {
  autoHideControls: false,
  showNavigationControl: true,
  showNavigator: false,
  showFullPageControl: true,
  showRotationControl: true,
  maxZoomLevel: 7,
  prefixUrl: `${process.env.PUBLIC_URL}/images/`
};

const ratio = 1;
const maxHeight = 675;

const setViewerDefaults = viewer => {
  viewer.gestureSettingsMouse.clickToZoom = false;
  viewer.gestureSettingsMouse.dblClickToZoom = true;
};

const disableInteraction = viewer => {
  viewer.gestureSettingsMouse.dblClickToZoom = false;
};

const enableInteraction = viewer => {
  viewer.gestureSettingsMouse.dblClickToZoom = true;
};

const generateManifestUrl = (obj, ts) => {
  let manifestURL = "";
  if (obj.URL_IIIF_MANIFEST) {
    manifestURL = obj.URL_IIIF_MANIFEST[0];
  } else {
    manifestURL = `/api/iiif/presentation/${obj.ID[0]}/info.json`;
  }
  return `${SEARCH.PATH_BASE}${manifestURL}?t=${ts}`;
};

const annotationStyle = {
  maxHeight: `${maxHeight}px`,
  border: "2px solid red",
  pointerEvents: "visible",
  cursor: "pointer"
};

const annotationHighlightStyle = {
  border: "4px solid red",
  backgroundColor: "rgba(0, 255, 0, 0.25)"
};

const newAddAnnotationHandler = (viewer, annotationContext) => event => {
  if (annotationContext.isEditing()) {
    const imageCoordinates = viewer.viewport.viewerElementToImageCoordinates(
      event.position
    );
    const dimensions = {
      x: imageCoordinates.x * ratio,
      y: imageCoordinates.y * ratio,
      w: 1000 * ratio,
      h: 1000 * ratio,
      ratio
    };
    annotationContext.setAnnotation(createAnnotation(viewer, dimensions));
  }
};

const currentTimestamp = () => (Date.now() / 1000) | 0;

const ZoomedImage = props => {
  const search = useSearcher();
  const [viewer, setViewer] = useState(null);
  const [timestamp, setTimestamp] = useState(currentTimestamp());
  const { showAnnotations } = useAnnotationsToggle();
  const annotationContext = useContext(AnnotationContext);
  const {
    annotation,
    setAnnotation,
    isEditing,
    isEditingAnnotation
  } = annotationContext;

  const isHighlighted = annotation => {
    const bodies = annotation.getBody();
    const resource = annotation.getResource();
    const searchTerm = search.searchTerm.toLowerCase();

    if (!searchTerm) {
      return false;
    }

    if (bodies.length) {
      return (
        bodies
          .map(body => body.__jsonld.value)
          .join(" ")
          .toLowerCase()
          .indexOf(searchTerm) !== -1
      );
    }

    if (resource && resource.getProperty("within")) {
      const toDisplay = resource.getProperty("within").toLowerCase();
      return toDisplay.description.indexOf(searchTerm) !== -1;
    }

    return (
      annotation
        .getProperty("description")
        .toLowerCase()
        .indexOf(searchTerm) !== -1
    );
  };

  const editableAnnotations = () => {
    if (!isEditingAnnotation(annotation)) {
      return [];
    }
    return [
      {
        annotation,
        on: {
          selector: {
            x: annotation.x,
            y: annotation.y,
            width: annotation.width,
            height: annotation.height
          }
        }
      }
    ];
  };

  useEffect(() => {
    // subscribe to the 'start annotating' button to disable/enable
    // certain interactions with the canvas (such as pan / zoom)
    if (viewer) {
      const subscription = annotationContext.subscribe("update.editing", v =>
        v ? disableInteraction(viewer) : enableInteraction(viewer)
      );
      return () => {
        annotationContext.unsubscribe("update.editing", subscription);
      };
    }
  }, [viewer, annotationContext]);

  useEffect(() => {
    const subscription = annotationContext.subscribe("save.annotation", () => {
      setTimestamp(currentTimestamp());
    });
    return () => {
      annotationContext.unsubscribe("save.annotation", subscription);
    };
  });

  useEffect(() => {
    // subscribe to the double-click event to add a new annotation
    // unsubscribe upon redraw to have the latest reference to the viewer
    if (viewer) {
      const addAnnotationHandler = newAddAnnotationHandler(
        viewer,
        annotationContext
      );
      viewer.addHandler("canvas-double-click", addAnnotationHandler);
      return () => {
        viewer.removeHandler("canvas-double-click", addAnnotationHandler);
      };
    }
  }, [viewer, annotationContext]);

  const manifestUrl = generateManifestUrl(props.obj, timestamp);
  return (
    <IIIFCanvas
      options={{
        ratio,
        maxHeight,
        osdOptions
      }}
      manifestUrl={manifestUrl}
      annotations={editableAnnotations()}
      includeManifestAnnotations
      renderAnnotation={({ annotation: a, on: { selector } }, idx) =>
        isEditingAnnotation(a) ? (
          <EditableAnnotation
            key={idx}
            ratio={ratio}
            annotation={a}
            setCoords={coords => {
              setAnnotation({ ...a, ...coords });
            }}
            boxStyles={getBoxStyles(a)}
            {...selector}
          >
            {a.body[1].value}
          </EditableAnnotation>
        ) : (
          <Annotation
            key={idx}
            ratio={ratio}
            annotation={a}
            onClick={() => {
              setAnnotation(a);
            }}
            style={{
              ...annotationStyle,
              display: showAnnotations ? "block" : "none",
              ...(isHighlighted(a) ? annotationHighlightStyle : {})
            }}
            {...selector}
          />
        )
      }
      renderAnnotationDetail={() => {
        if (annotation && !isEditingAnnotation(annotation)) {
          return (
            <AnnotationDetailPopup
              annotation={annotation}
              onClose={() => setAnnotation(null)}
              open
            />
          );
        }
        return null;
      }}
      renderNavigation={props => {
        return <CanvasNavigation {...props} />;
      }}
      initViewer={viewer => {
        setViewerDefaults(viewer);
        if (isEditing()) {
          disableInteraction(viewer);
        }
        setViewer(viewer);
      }}
    />
  );
};

export default ZoomedImage;
